import { z } from 'zod';

export const APPS_VERSIONS_FIELDS = {
  apk: 'apk',
  platform: 'platform',
  appName: 'appName',
  downloadUrl: 'downloadUrl',
  versionCode: 'versionCode',
  versionName: 'versionName',
  minSupportedVersionCode: 'minSupportedVersionCode',
  forceUpdate: 'forceUpdate',
  enabled: 'enabled',
} as const;

export type AppsVersionsField =
  (typeof APPS_VERSIONS_FIELDS)[keyof typeof APPS_VERSIONS_FIELDS];

export const PLATFORMS = {
  ANDROID: 'android',
  IOS: 'ios',
} as const;

export const APPLICATIONS = {
  DRIVER: 'driver',
  CUSTOMER: 'customer',
  ADMIN: 'admin',
  OPERATIONS: 'operations',
} as const;

export type Application = (typeof APPLICATIONS)[keyof typeof APPLICATIONS];

export type Platform = (typeof PLATFORMS)[keyof typeof PLATFORMS];

export interface AppsVersionsApi {
  id: string;
  platform: Platform;
  appName: Application;
  versionCode: string;
  versionName: string;
  downloadUrl: string;
  minSupportedVersionCode: string;
  forceUpdate: boolean;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AppsVersionsFilter {
  platform: string;
  appName: string;
  versionCode: string;
  versionName: string;
  minSupportedVersionCode: string;
  enabled?: boolean;
}

const appVersionFullSchema = z.object({
  apk: z.instanceof(File).optional(),
  platform: z.string().min(1, 'La plataforma es requerida'),
  appName: z.string().min(1, 'El nombre de la aplicación es requerido'),
  versionCode: z.string().min(1, 'El código de la versión es requerido'),
  versionName: z
    .string()
    .min(1, 'El nombre de la versión es requerido')
    .max(20, 'El nombre de la versión no puede exceder 20 caracteres'),
  minSupportedVersionCode: z
    .string()
    .min(1, 'El código de la versión mínima soportada es requerido'),
  forceUpdate: z.boolean(),
  enabled: z.boolean(),
  downloadUrl: z.string().optional(),
  sha256: z.string().optional(),
});

export const appsVersionsSchema = appVersionFullSchema.extend({
  apk: z.instanceof(File, { message: 'El APK es requerido' }),
}).omit({
  downloadUrl: true,
  sha256: true,
});

export type AppsVersionsSchema = z.infer<typeof appsVersionsSchema>;

export const formAppsVersionsDefaultValues: Partial<AppsVersionsSchema> = {
  apk: undefined,
  platform: PLATFORMS.ANDROID,
  appName: APPLICATIONS.DRIVER,
  versionCode: '',
  versionName: '',
  minSupportedVersionCode: '',
  forceUpdate: false,
};

export const updateAppVersionSchema = appVersionFullSchema.omit({
  apk: true,
  downloadUrl: true,
  sha256: true,
});

export type UpdateAppVersionSchema = z.infer<typeof updateAppVersionSchema>;

export const updateAppVersionWithApkSchema = appVersionFullSchema;

export type UpdateAppVersionWithApkSchema = z.infer<typeof updateAppVersionWithApkSchema>;
